import { useState } from 'react';
import { App, Button, Card, Collapse, Form, Result, theme, Typography } from 'antd';
import { VehicleStep } from './steps/VehicleStep';
import { TakerStep } from './steps/TakerStep';
import { ConfirmationStep } from './steps/ConfirmationStep';
import { StepControls } from './StepControls';
import { getStepCollapseItem } from './getStepCollapseItem';
import { QuotationLoadingScreen } from 'Pages/QuotationPage/components/QuotationLoadingScreen';
import { useLoading } from '@shared/hooks/useLoading';
import { useCreateVehicle } from '@features/vehicles/hooks/useVehicles';
import { useCreateOrUpdateClient } from '@features/clients/hooks/useClients';
import { useCreateQuotation } from '@features/quotations/hooks/useQuotations';
import { getErrorMessage } from '@shared/utils/errors/getErrorMessage';
import { SUCCESS_COLOR } from '@shared/constants/colors';
import { STEPS_CONFIG, CONFIRMATION_STEP_CONFIG, TAKER_STEP_CONFIG, VEHICLE_STEP_CONFIG } from 'Pages/QuotationPage/constants/stepsConfig';
import { useNavigate } from 'react-router-dom';

// TODO: Pendiente pasar las solicitudes a tanStack Query usando hooks y reemplazar Redux por Zustand para el manejo de estados
import { useDispatch, useSelector } from 'react-redux';
import { getQuotationPrice } from 'Pages/DataGatewayPage/routerForm/utils';
import { initializeExpectedPackagesResponsesAction, setPetitionAliveAction } from 'Redux/actions/comparationQuotationActions'
import { generateInsuranceQuotation } from 'Redux/actions/globalQuotation';
import { ROUTES } from '@/App/Router/routes';

const { Text } = Typography;

export const CreateQuotationMain = ({ currentStep, onNext, onPrevious, isExternal }) => {
  const [externalQuotationCreated, setExternalQuotationCreated] = useState(false);

  const { token: themeToken } = theme.useToken();
  const { notification } = App.useApp();
  const generateQuotationsByInsuranceCarrierLoading = useLoading();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedBranch = useSelector((state) => state.globals.selected_sede); // Se recupera la sede seleccionada en la app desde redux

  const [quotationContext, setQuotationContext] = useState({
    beneficiary: {},
    isBeneficiarySameAsTaker: true,
    circulationCityName: '',
  });

  const [vehicleForm] = Form.useForm();
  const [takerForm] = Form.useForm();
  const [beneficiaryForm] = Form.useForm();

  const createVehicleMutation = useCreateVehicle();
  const createOrUpdateClientMutation = useCreateOrUpdateClient();
  const createQuotationMutation = useCreateQuotation();

  const isLoading = createVehicleMutation.isPending || createOrUpdateClientMutation.isPending || createQuotationMutation.isPending || generateQuotationsByInsuranceCarrierLoading.isLoading;

  const updateQuotationContext = (newContext) => {
    setQuotationContext((prev) => ({ ...prev, ...newContext }));
  }

  // Funciones de validación de los formularios
  const validationsByStep = {
    [VEHICLE_STEP_CONFIG.key]: async () => {
      await vehicleForm.validateFields();
    },
    [TAKER_STEP_CONFIG.key]: async () => {
      if (quotationContext.isBeneficiarySameAsTaker) {
        await takerForm.validateFields();
        updateQuotationContext({ beneficiary: {} }); // Se limpia 'beneficiary' si es el mismo que el tomador
        return;
      }

      // Se validan los formularios del tomador y el beneficiario
      const [_, beneficiary] = await Promise.all([
        takerForm.validateFields(),
        beneficiaryForm.validateFields(),
      ]);
      updateQuotationContext({ beneficiary });
    },
  };

  const handleNext = async () => {
    const { key: stepKey } = STEPS_CONFIG[currentStep]; // Obtiene la key del step actual
    await validationsByStep[stepKey]?.(); // Ejecuta la validación del step correspondiente si existe
    onNext();

    // Si se está en el paso de confirmación, se ejecutan las mutaciones
    if (stepKey === CONFIRMATION_STEP_CONFIG.key) {
      let createdQuotation;

      // Se crea el vehículo, tomador y la cotización
      try {
        const savedClient = await createOrUpdateClientMutation.mutateAsync(takerForm.getFieldsValue()); // Se crea/actualiza el tomador de primero porque es más propenso a fallar por validaciones
        const createdVehicle = await createVehicleMutation.mutateAsync(vehicleForm.getFieldsValue());

        const externalQuotation = localStorage.getItem('external_session') === 'true'
        createdQuotation = await createQuotationMutation.mutateAsync({
          vehicleId: createdVehicle.id,
          takerId: savedClient.id,
          isBeneficiarySameAsTaker: quotationContext.isBeneficiarySameAsTaker,
          beneficiary: quotationContext.beneficiary,
          externalQuotation,
          branchId: selectedBranch?.id,
          ...(externalQuotation && { notificationLink: `${window.location.origin}${ROUTES.QUOTATIONS.UPDATE('{id}')}` }),
        });

        notification.success({
          message: <Text strong>Cotización creada</Text>,
          description: '¡La cotización se ha creado correctamente!',
        });
      } catch (error) {
        notification.error({
          message: <Text strong>Cotización no creada</Text>,
          description: getErrorMessage(error),
        });

        onPrevious();
        return;
      };

      // Si es una cotización externa no se generan las cotizaciones por aseguradora
      if (isExternal) {
        setExternalQuotationCreated(true);
        return;
      }

      // Se generan las cotizaciones por aseguradora
      generateQuotationsByInsuranceCarrierLoading.start();
      try {
        const price = await getQuotationPrice(false);
        const { insurance_carriers: quotedInsuranceCarriers } = await dispatch(generateInsuranceQuotation(createdQuotation.id, price));
        dispatch(initializeExpectedPackagesResponsesAction(quotedInsuranceCarriers));
        dispatch(setPetitionAliveAction(true));
        // localStorage.setItem('consulting', true);
        navigate(ROUTES.QUOTATIONS.PLANS.LIST(createdQuotation.id), { replace: true });
      } catch (error) {
        notification.error({
          message: 'No fue posible generar las cotizaciones por aseguradora',
          description: getErrorMessage(error),
        });
      } finally {
        generateQuotationsByInsuranceCarrierLoading.stop();
      };
    }
  };

  const handleCirculationCityChange = (_, option) => {
    const name = option?.record.name ?? '';
    updateQuotationContext({ circulationCityName: name });
  }

  const handleBeneficiarySameAsTakerChange = (value) => {
    updateQuotationContext({ isBeneficiarySameAsTaker: value });
  }

  const componentsByStep = {
    [VEHICLE_STEP_CONFIG.key]: <VehicleStep form={vehicleForm} onCirculationCityChange={handleCirculationCityChange} />,
    [TAKER_STEP_CONFIG.key]: (
      <TakerStep
        form={takerForm}
        beneficiaryForm={beneficiaryForm}
        isBeneficiarySameAsTaker={quotationContext.isBeneficiarySameAsTaker}
        onBeneficiarySameAsTakerChange={handleBeneficiarySameAsTakerChange}
        isExternal={isExternal}
      />
    ),
    [CONFIRMATION_STEP_CONFIG.key]: (
      <ConfirmationStep
        vehicle={{
          ...vehicleForm.getFieldsValue(),
          circulationCityName: quotationContext.circulationCityName,
        }}
        taker={takerForm.getFieldsValue()}
        isExternal={isExternal}
      />
    ),
  };

  const items = STEPS_CONFIG.map(({ key, icon, title }, step) => {
    const isLastStep = step === STEPS_CONFIG.length - 1;

    return getStepCollapseItem({
      step,
      currentStep,
      icon,
      title,
      primaryColor: isLastStep ? SUCCESS_COLOR : themeToken.colorPrimary, // El ultimo paso usa el color SUCCESS_COLOR
      children: componentsByStep[key],
    })
  });

  return (<>
    <Card>
      <Collapse
        activeKey={currentStep}
        bordered={false}
        expandIcon={() => null} // Oculta el ícono de expandir/colapsar, la navegación se gestiona únicamente con StepControls
        collapsible='icon' // Al no renderizarse el ícono, se deshabilita también la interacción en el header (elimina el cursor pointer)
        items={items}
        style={{ background: themeToken.colorBgContainer }}
      />
      {currentStep < items.length && ( // Muestra los controles de navegación solo si no se han completado todos los pasos
        <StepControls current={currentStep} total={items.length} onNext={handleNext} onPrevious={onPrevious} isExternal={isExternal} />
      )}
    </Card>
    {isExternal && externalQuotationCreated && (
      <Result
        status='success'
        title='¡Tu cotización se ha creado correctamente!'
        subTitle='Ya tenemos toda la información. Un asesor la revisará y se pondrá en contacto contigo muy pronto.'
        extra={
          <Button shape='round' onClick={() => navigate(-1)}>
            Finalizar
          </Button>
        }
      />
    )}
    <QuotationLoadingScreen loading={isLoading} isExternal={isExternal} />
  </>);
};